import {
  CombinedInspection,
  ReportInspectionType,
  ReportTemplate,
} from 'features/reporting/types'
import {
  ReportTemplateId,
  ReportTemplates,
} from 'pages/reporting/shared/reportTemplates'
import {
  GeckoAssetModel,
  GeckoInspection,
  GeckoVisualInspection,
  RolodexEntryType,
  VisualInspectionType,
} from 'types'

interface ReportPdfFilenameParts {
  organizationName?: string
  siteName?: string
  assetName?: string
  revisionNumber: number
}

const sanitizeFilenamePart = (value: string | undefined): string => {
  const sanitized = value
    ?.trim()
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')

  return sanitized || 'Unknown'
}

export const buildReportPdfFilename = ({
  organizationName,
  siteName,
  assetName,
  revisionNumber,
}: ReportPdfFilenameParts): string =>
  [
    sanitizeFilenamePart(organizationName),
    sanitizeFilenamePart(siteName),
    sanitizeFilenamePart(assetName),
    `Rev.${revisionNumber}`,
  ].join('_')

interface InspectionDataEntry {
  tags: Record<string, string | undefined>
}

export function getInspectionIdsWithData(
  entries: ReadonlyArray<InspectionDataEntry>
): Set<string> {
  return new Set(
    entries
      .map((entry) => entry.tags.inspection)
      .filter((inspectionId): inspectionId is string => Boolean(inspectionId))
  )
}

const visualInspectionTypeToReportInspectionType: Record<
  VisualInspectionType,
  ReportInspectionType
> = {
  external: 'external-visual',
  internal: 'internal-visual',
}

export function getReportInspectionType(
  inspection: GeckoInspection | GeckoVisualInspection
): ReportInspectionType | undefined {
  if (!inspection.data.inspection_type) {
    return undefined
  }
  if (inspection.type === RolodexEntryType.VISUAL_INSPECTION) {
    return visualInspectionTypeToReportInspectionType[
      inspection.data.inspection_type as VisualInspectionType
    ]
  }
  return inspection.data.inspection_type
}

interface GetCompatibleTemplatesParams {
  selectedInspections: string[]
  allInspections: CombinedInspection[]
  assetModelsByUnit: Map<string, GeckoAssetModel>
}

export function getCompatibleTemplates({
  selectedInspections,
  allInspections,
  assetModelsByUnit,
}: GetCompatibleTemplatesParams): Array<[ReportTemplateId, ReportTemplate]> {
  if (selectedInspections.length === 0) return []

  const selectedInspectionData = allInspections.filter((insp) =>
    selectedInspections.includes(insp.id)
  )

  return Object.entries(ReportTemplates).filter(([, template]) => {
    if (
      selectedInspectionData.length > 1 &&
      !template.canSupportMultipleInspections
    ) {
      return false
    }

    const allInspectionsSupported = selectedInspectionData.every(
      (inspection) => {
        const inspectionType = getReportInspectionType(inspection)
        return (
          inspectionType &&
          template.supportedInspectionTypes.includes(inspectionType)
        )
      }
    )

    if (!allInspectionsSupported) return false

    const ndtInspections = selectedInspectionData.filter(
      (insp) => insp.kind === 'NDT'
    )

    if (ndtInspections.length > 0) {
      const allComponentsSupported = ndtInspections.every((inspection) => {
        const assetModel = assetModelsByUnit.get(inspection.tags.unit)
        const componentModel = assetModel?.data.components.get(
          inspection.tags.component
        )
        if (!componentModel) return false
        return template.supportedComponentTypes.includes(componentModel.type)
      })
      return allComponentsSupported
    }

    return true
  }) as Array<[ReportTemplateId, ReportTemplate]>
}
